import { useState, useEffect } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Flame, TrendingDown, TrendingUp, Minus, Loader2 } from 'lucide-react';

export function CoachPunchCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/coaching/punch')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg px-4 py-3 flex items-center gap-3" data-testid="coach-punch-loading">
        <div className="w-8 h-8 rounded-full bg-[#FF6B35]/15 flex items-center justify-center flex-shrink-0">
          <Loader2 className="w-4 h-4 text-[#FF6B35] animate-spin" />
        </div>
        <p className="text-[10px] text-zinc-600 italic">Namaste contemple ton chemin...</p>
      </div>
    );
  }

  if (!data?.punch) return null;

  const { punch, weight_change, fat_change, muscle_change, objective } = data;

  const TrendIcon = ({ val, invert }) => {
    if (!val || val === 0) return <Minus className="w-3 h-3 text-zinc-500" />;
    const isGood = invert ? val < 0 : val > 0;
    return isGood
      ? <TrendingUp className="w-3 h-3 text-green-400" />
      : <TrendingDown className="w-3 h-3 text-red-400" />;
  };

  const hasDelta = (weight_change !== null && weight_change !== 0) || (fat_change !== null && fat_change !== 0) || (muscle_change !== null && muscle_change !== 0);

  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg overflow-hidden" data-testid="coach-punch-card">
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[#FF6B35]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Flame className="w-4 h-4 text-[#FF6B35]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-[#FF6B35] font-bold uppercase tracking-wider mb-1">Coach Namaste 🙏</p>
          <p className="text-xs text-zinc-300 leading-relaxed" data-testid="punch-message">
            {punch}
          </p>
        </div>
      </div>
      {hasDelta && (
        <div className="px-4 pb-3 flex gap-3 flex-wrap">
          {weight_change !== null && (
            <div className="flex items-center gap-1 text-[10px]">
              <TrendIcon val={weight_change} invert={objective !== 'bulk'} />
              <span className="text-zinc-500">Poids</span>
              <span className={`font-mono font-bold ${
                (['cut', 'perte'].includes(objective) && weight_change < 0) || (objective === 'bulk' && weight_change > 0)
                  ? 'text-green-400' : weight_change === 0 ? 'text-zinc-400' : 'text-[#FFB800]'
              }`}>
                {weight_change > 0 ? '+' : ''}{weight_change}kg
              </span>
            </div>
          )}
          {fat_change !== null && (
            <div className="flex items-center gap-1 text-[10px]">
              <TrendIcon val={fat_change} invert={true} />
              <span className="text-zinc-500">Gras</span>
              <span className={`font-mono font-bold ${fat_change < 0 ? 'text-green-400' : fat_change === 0 ? 'text-zinc-400' : 'text-red-400'}`}>
                {fat_change > 0 ? '+' : ''}{fat_change}%
              </span>
            </div>
          )}
          {muscle_change !== null && (
            <div className="flex items-center gap-1 text-[10px]">
              <TrendIcon val={muscle_change} invert={false} />
              <span className="text-zinc-500">Muscle</span>
              <span className={`font-mono font-bold ${muscle_change > 0 ? 'text-green-400' : muscle_change === 0 ? 'text-zinc-400' : 'text-red-400'}`}>
                {muscle_change > 0 ? '+' : ''}{muscle_change}kg
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
